// frontend/src/hooks/useInstallPrompt.ts
import { useState, useEffect } from 'react';

interface InstallPromptState {
    /** True when the browser has fired beforeinstallprompt and install is possible. */
    isInstallable: boolean;
    /** True when the app is already running in standalone (installed) mode. */
    isInstalled: boolean;
    /** True on iOS Safari where beforeinstallprompt is not supported — show manual guide instead. */
    isIOSSafari: boolean;
    /** Call to trigger the native browser install dialog. Returns the user's choice. */
    promptInstall: () => Promise<'accepted' | 'dismissed' | null>;
}

/**
 * Manages the PWA "Add to Home Screen" install prompt lifecycle.
 *
 * - Captures the `beforeinstallprompt` event (Chrome/Edge/Android).
 * - Detects iOS Safari for manual install guidance.
 * - Detects when the app is already running in standalone (installed) mode.
 */
export const useInstallPrompt = (): InstallPromptState => {
    const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
    const [isInstalled, setIsInstalled] = useState(false);

    // Detect iOS Safari (doesn't support beforeinstallprompt)
    const isIOSSafari =
        typeof navigator !== 'undefined' &&
        /iP(hone|ad|od)/.test(navigator.userAgent) &&
        !('MSStream' in window) &&
        // @ts-ignore
        !navigator.standalone;

    useEffect(() => {
        // Already running in standalone mode (installed)
        if (window.matchMedia('(display-mode: standalone)').matches) {
            setIsInstalled(true);
            return;
        }

        const handleBeforeInstall = (e: Event) => {
            e.preventDefault();
            setDeferredPrompt(e);
        };

        const handleAppInstalled = () => {
            setIsInstalled(true);
            setDeferredPrompt(null);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstall);
        window.addEventListener('appinstalled', handleAppInstalled);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
            window.removeEventListener('appinstalled', handleAppInstalled);
        };
    }, []);

    const promptInstall = async (): Promise<'accepted' | 'dismissed' | null> => {
        if (!deferredPrompt) return null;
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        setDeferredPrompt(null);
        return outcome as 'accepted' | 'dismissed';
    };

    return {
        isInstallable: !!deferredPrompt,
        isInstalled,
        isIOSSafari,
        promptInstall,
    };
};
